<script setup lang='ts'>
import { notify } from '@kyvg/vue3-notification';
import { computed, ref } from 'vue';
import { Link, router } from '@inertiajs/vue3'
import { InvoiceResource, OrdersResource } from '@/types/Resources';
import { useFormatTime } from '@/Composables/useFormatTime';
import { useFormatCurrency } from '@/Composables/useFormatCurrency';
import { useFormatNumber } from '@/Composables/useFormatNumber';
import Icon from '@/Components/Utils/Icon.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue';

import { faCommentAlt, faTimes, faPencil, faCheckCircle } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faCommentAlt, faTimes, faPencil, faCheckCircle)

const props = defineProps<{
    order: OrdersResource
    isLoadingEditOrder?: number | null
    index: number
    invoice: InvoiceResource
    selectedEditOrder: OrdersResource | null
    isModalEditOrder: boolean
}>()

const emits = defineEmits<{
    (e: 'selectedEditOrder', value: OrdersResource): void
    (e: 'isModalEditOrder', value: boolean): void
}>()

// Computed display values — invoice overrides take precedence
const displayQuantity = computed(() => props.order.quantity_in_invoice ?? props.order.quantity)
const displayPrice = computed(() => props.order.product_price_in_invoice ?? props.order.product_price)
const displayTotal = computed(() => props.order.total_amount_in_invoice ?? props.order.total_amount)
const hasDiscount = computed(() => (props.order.discount_in_percentage ?? 0) > 0)
const finalTotal = computed(() => {
    const total = displayTotal.value
    if (hasDiscount.value) {
        return total - (total * ((props.order.discount_in_percentage ?? 0) / 100))
    }
    return total
})

// Section: Unselect order
const isLoadingUnselectOrder = ref<number[]>([])
const onUnselectOrder = (idInvoice: number, order_id: number) => {
    if (!idInvoice) {
        notify({
            title: 'Pilih invoice terlebih dahulu',
            text: '',
            type: 'error'
        })
        return
    }
    router.patch(
        route('casheer.invoices.unselect_order', [idInvoice]),
        { order: order_id },
        {
            onStart: () => isLoadingUnselectOrder.value.push(order_id),
            onFinish: () => {
                const index = isLoadingUnselectOrder.value.indexOf(order_id)
                if (index > -1) isLoadingUnselectOrder.value.splice(index, 1)
            },
            onError: () => notify({ title: 'Oops!', text: 'Gagal menghapus order', type: 'error' }),
        }
    )
}

const onOpenEditOrder = () => {
    emits('selectedEditOrder', {
        ...props.order,
        // Aliases for compatibility with OrdersList edit modal
        quantity_show: displayQuantity.value,
        quantity_by_type: props.order.quantity,
        product_price_show: displayPrice.value,
        is_have_discount: hasDiscount.value,
    })
    emits('isModalEditOrder', true)
}
</script>

<template>
    <li
        class="first:border-t-0 border-t border-t-gray-300 relative py-3 flex flex-col lg:flex-row gap-x-3 pr-2"
        :class="hasDiscount ? 'border-l-4 border-l-sky-300 bg-sky-50' : ''"
    >
        <div v-if="isLoadingEditOrder === order?.id" class="absolute inset-0 opacity-50">
            <div class="skeleton h-full"></div>
        </div>

        <div class="px-3 flex-grow grid grid-cols-4 lg:grid-cols-7 gap-x-4">
            <!-- Column: Order info -->
            <div class="col-span-3 flex gap-x-2">
                <div class="text-gray-500">{{ index + 1 }}.</div>
                <div class="space-y-0.5">
                    <h3 class="font-bold flex items-center gap-1.5 flex-wrap">
                        <Link :href="route('casheer.orders.show', order.slug)" class="primaryLink">
                            {{ order.reference }}
                        </Link>
                        <span v-if="hasDiscount" class="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700">
                            {{ order.discount_in_percentage }}%
                        </span>
                        <FontAwesomeIcon
                            v-if="order.is_manufacture_finished"
                            v-tooltip="'Manufacture selesai'"
                            icon='fal fa-check-circle'
                            class='text-green-500'
                            fixed-width
                            aria-hidden='true'
                        />
                    </h3>

                    <!-- Product name -->
                    <p class="text-gray-500">
                        <Icon :data="order.type_icon" />
                        {{ order.product_name }}
                    </p>

                    <!-- Note -->
                    <p v-if="order.file_merek" class="text-gray-500 text-sm">
                        <span v-tooltip="'File merek'">
                            <FontAwesomeIcon icon='fal fa-file' fixed-width aria-hidden='true' />
                        </span>
                        <span class="ml-1">{{ order.file_merek }}</span>
                    </p>

                    <!-- Manufacture state -->
                    <p v-if="order.manufacture_state_icon" class="" :class="order.manufacture_state_icon?.class">
                        <Icon :data="order.manufacture_state_icon" />
                        {{ order.manufacture_state_icon?.tooltip }}
                    </p>

                    <!-- Mobile: price & qty -->
                    <p class="lg:hidden text-gray-500 tabular-nums text-sm">
                        <FontAwesomeIcon icon='fal fa-dollar-sign' fixed-width aria-hidden='true' />
                        <span class="ml-1">{{ useFormatNumber(displayQuantity) }} pcs</span>,
                        <span>{{ useFormatCurrency(displayPrice) }}/pcs</span>
                    </p>

                    <p class="text-gray-500 italic text-xs">
                        <FontAwesomeIcon v-tooltip="'Tanggal Order dibuat'" icon='fal fa-calendar-alt' fixed-width aria-hidden='true' />
                        <span class="ml-2">{{ useFormatTime(order.created_at) }}</span>
                    </p>
                </div>
            </div>

            <!-- Column: Price per unit -->
            <p class="hidden lg:block flex-none text-right font-medium tabular-nums">
                <template v-if="order.product_price_in_invoice && order.product_price_in_invoice !== order.product_price">
                    <span class="line-through text-gray-400 block">{{ useFormatCurrency(order.product_price) }}/pcs</span>
                    <span class="text-sky-600">{{ useFormatCurrency(order.product_price_in_invoice) }}/pcs</span>
                </template>
                <template v-else>
                    {{ useFormatCurrency(order.product_price) }}/pcs
                </template>
            </p>

            <!-- Column: Quantity -->
            <p class="hidden lg:block flex-none text-right font-medium tabular-nums">
                <template v-if="order.quantity_in_invoice && order.quantity_in_invoice !== order.quantity">
                    <span class="line-through text-gray-400 block">{{ useFormatNumber(order.quantity) }} pcs</span>
                    <span class="text-sky-600">{{ useFormatNumber(order.quantity_in_invoice) }} pcs</span>
                </template>
                <template v-else>
                    {{ useFormatNumber(order.quantity) }} pcs
                </template>
            </p>

            <!-- Column: Total -->
            <p class="lg:col-span-2 text-right font-medium tabular-nums">
                <span :class="hasDiscount ? 'line-through text-gray-400' : ''">
                    {{ useFormatCurrency(displayTotal) }}
                </span>
                <span v-if="hasDiscount" class="text-sky-600 font-semibold block">
                    {{ useFormatCurrency(finalTotal) }}
                </span>
            </p>
        </div>

        <!-- Button: unselect & edit -->
        <div class="w-fit flex self-end lg:self-start flex-row lg:flex-col flex-wrap flex-shrink items-end gap-x-1 gap-y-1.5">
            <div
                v-if="invoice.billing_status === 'unpaid'"
                v-tooltip="'Hapus dari list'"
                @click="onUnselectOrder(invoice.id, order.id)"
                class="h-fit rounded hover:bg-red-200 border border-transparent hover:border-red-500 text-red-400 hover:text-red-600 text-lg cursor-pointer px-2 py-1"
            >
                <FontAwesomeIcon v-if="!isLoadingUnselectOrder.includes(order.id)" icon='fal fa-times' fixed-width aria-hidden='true' />
                <LoadingIcon v-else />
            </div>
            <div
                v-if="invoice.billing_status === 'unpaid'"
                v-tooltip="'Edit Order ini'"
                @click="onOpenEditOrder"
                class="h-fit rounded hover:bg-gray-200 border border-transparent hover:border-gray-500 text-gray-400 hover:text-gray-600 text-lg cursor-pointer px-2 py-1"
            >
                <FontAwesomeIcon icon='fal fa-pencil' fixed-width aria-hidden='true' />
            </div>
            <div
                v-if="invoice.billing_status !== 'unpaid'"
                v-tooltip="'Terkunci. Sudah ada pembayaran (sebagian/lunas) di Invoice.'"
                class="h-fit rounded text-amber-500 text-lg xcursor-pointer px-2"
            >
                <FontAwesomeIcon icon='fal fa-lock' fixed-width aria-hidden='true' />
            </div>
        </div>
    </li>
</template>
